import React, { useState } from 'react';
import { Box } from '@mui/system';
import { Card, Chip, Stack, Typography } from '@mui/joy';
import { KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui-symbols-material/w300';
import { useTranslation } from 'react-i18next';
import { changelog } from '../../../generated/changelog';
import AboutWelcomeSection from '../../../components/WelcomeHero/AboutWelcomeSection';

const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

// Only the newest released version starts expanded; older ones start collapsed.
const releasedVersions = changelog.filter(
    (version) => version.groups.length > 0 && version.version !== 'Unreleased'
);
const initialCollapsed: Record<string, boolean> = Object.fromEntries(
    releasedVersions.slice(1).map((version) => [version.version, true])
);

const About = () => {
    const { t } = useTranslation();
    // Released versions are collapsible; Unreleased is always expanded.
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>(initialCollapsed);
    const toggle = (version: string) =>
        setCollapsed((prev) => ({ ...prev, [version]: !prev[version] }));

    return (
        <Box sx={{ overflowY: 'auto', height: '100%', px: 3, py: 2, maxWidth: 1000 }}>
            <Stack spacing={3}>
                <AboutWelcomeSection />
                <Box>
                    <Typography level="titleMedium">{t('About')}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography level="body-sm" sx={{ color: '#666' }}>{t('Version')}</Typography>
                        <Chip size="sm" variant="soft" color="primary">{APP_VERSION}</Chip>
                    </Box>
                </Box>

                <Stack spacing={2}>
                    <Typography level="titleSmall">{t('Changelog')}</Typography>

                    {changelog.filter((version) => version.groups.length > 0).map((version) => {
                        const isUnreleased = version.version === 'Unreleased';
                        const isCollapsible = !isUnreleased;
                        const isOpen = !isCollapsible || !collapsed[version.version];
                        return (
                            <Card
                                key={version.version}
                                variant="outlined"
                                sx={{ borderRadius: '4px', overflow: 'hidden', '--Card-padding': '0px', gap: 0, backgroundColor: '#fff' }}
                            >
                                <Box
                                    onClick={isCollapsible ? () => toggle(version.version) : undefined}
                                    sx={{
                                        px: 2,
                                        py: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        backgroundColor: '#f5f5f5',
                                        borderBottom: isOpen ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                        cursor: isCollapsible ? 'pointer' : 'default',
                                        userSelect: 'none',
                                        '&:hover': isCollapsible ? { backgroundColor: '#eee' } : undefined,
                                    }}
                                >
                                    <Typography level="headline" sx={{ color: '#333' }}>
                                        {isUnreleased ? t('Unreleased') : `${t('Version')} ${version.version}`}
                                    </Typography>
                                    {isUnreleased && (
                                        <Chip size="sm" variant="soft" color="warning">{t('upcoming')}</Chip>
                                    )}
                                    <Box sx={{ flex: 1 }} />
                                    {version.date && (
                                        <Typography level="body-sm" sx={{ color: '#666' }}>{version.date}</Typography>
                                    )}
                                    {isCollapsible && (
                                        isOpen
                                            ? <KeyboardArrowUpRounded sx={{ color: '#666', fontSize: 20 }} />
                                            : <KeyboardArrowDownRounded sx={{ color: '#666', fontSize: 20 }} />
                                    )}
                                </Box>

                                {isOpen && (
                                <Stack sx={{ px: 2, py: 1.5, gap: 2 }}>
                                    {version.groups.map((group, gi) => (
                                        <Box key={`${version.version}-${gi}`}>
                                            {group.title && (
                                                <Typography
                                                    level="body-xs"
                                                    fontWeight="lg"
                                                    sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.5 }}
                                                >
                                                    {group.title}
                                                </Typography>
                                            )}
                                            <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                                                {group.items.map((item, ii) => (
                                                    <Typography
                                                        component="li"
                                                        level="bodySmall"
                                                        sx={{ color: '#333', display: 'list-item', mb: '2px', '&::marker': { color: '#999' } }}
                                                        key={`${version.version}-${gi}-${ii}`}
                                                    >
                                                        {item}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                                )}
                            </Card>
                        );
                    })}
                </Stack>
            </Stack>
        </Box>
    );
};

export default About;
